/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { CSSProperties, Children, useEffect, useRef } from 'react';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import {
  Navigation,
  Pagination,
  Scrollbar,
  A11y,
  Mousewheel,
  FreeMode,
} from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';

import { useCarouselContextV2 } from './CarouselV2ContextProvider';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/a11y';
import 'swiper/css/mousewheel';
import 'swiper/css/free-mode';

interface CarouselV2Props {
  /** Carousel child elements */
  children: React.ReactNode;
  /** Custom styles for the container */
  containerStyle?: CSSProperties;
  /** Custom styles for the slide items */
  slideStyle?: CSSProperties;
  /** Swiper options */
  swiperOptions?: SwiperProps & SwiperOptions;
}

const swiperOptionsWithDefaults: SwiperProps & SwiperOptions = {
  spaceBetween: 16,
  direction: 'horizontal',
  slidesPerView: 'auto',
  navigation: false,
  freeMode: {
    enabled: true,
    sticky: true,
  },
  mousewheel: {
    forceToAxis: true, // Force scrolling only on horizontal axis
    sensitivity: 1,
    releaseOnEdges: false,
  },
  // Enable keyboard navigation
  keyboard: {
    enabled: true,
    onlyInViewport: true,
  },
  grabCursor: true,
  // Allow scrolling to the edges
  cssMode: false,
  // Ensure slides can be scrolled to the edge
  centeredSlides: false,
  centeredSlidesBounds: false,
  style: {
    zIndex: 0,
  },
};

/**
 * CarouselV2 - A reusable carousel component with swiping support
 *
 * Features:
 * - Deep nested arrow button support
 * - Built on Swiper.js for reliability
 */
export const CarouselV2 = ({
  children,
  containerStyle = {},
  slideStyle = {},
  swiperOptions = {},
}: CarouselV2Props): React.JSX.Element => {
  const carouseListRef = useRef<HTMLDivElement>(null);
  const { handleSwiper } = useCarouselContextV2();

  const _swiperOptions = {
    ...swiperOptionsWithDefaults,
    ...swiperOptions,
    style: {
      ...swiperOptionsWithDefaults.style,
      ...swiperOptions.style,
    },
  };

  useEffect(() => {
    const el = carouseListRef.current;

    const wheelHandler = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault(); // block browser nav gesture
      }
    };

    el?.addEventListener('wheel', wheelHandler, { passive: false });

    return () => el?.removeEventListener('wheel', wheelHandler);
  }, []);

  const swiperModules = [];
  if (_swiperOptions.navigation) swiperModules.push(Navigation);
  if (_swiperOptions.pagination) swiperModules.push(Pagination);
  if (_swiperOptions.scrollbar) swiperModules.push(Scrollbar);
  if (_swiperOptions.mousewheel) swiperModules.push(Mousewheel);
  if (_swiperOptions.freeMode) swiperModules.push(FreeMode);

  swiperModules.push(A11y); // Always include accessibility

  const swiperConfig: SwiperOptions = {
    modules: swiperModules,
    ..._swiperOptions,
  };

  return (
    <div
      data-module="CarouselList"
      ref={carouseListRef}
      style={{ ...containerStyle }}
    >
      <Swiper {...swiperConfig} onSwiper={handleSwiper}>
        {Children.map(children, (child, index) => (
          <SwiperSlide key={index} style={slideStyle}>
            {child}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default CarouselV2;
